import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Play, 
  Pause, 
  Trash2,
  Phone,
  MessageSquare,
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Users
} from 'lucide-react';

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh for active campaigns
    const interval = setInterval(() => {
      if (campaign?.status === 'active') {
        fetchData(true);
      }
    }, 3000);
    
    return () => clearInterval(interval);
  }, [id, campaign?.status]);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    
    try {
      const [campaignRes, logsRes] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/logs`)
      ]);
      setCampaign(campaignRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      if (!silent) {
        toast.error('Kampanya bulunamadı');
        navigate('/campaigns');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleStartCampaign = async () => {
    try {
      await api.post(`/campaigns/${id}/start`);
      toast.success('Kampanya başlatıldı');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kampanya başlatılamadı');
    }
  };

  const handlePauseCampaign = async () => {
    try {
      await api.post(`/campaigns/${id}/pause`);
      toast.success('Kampanya duraklatıldı');
      fetchData();
    } catch (error) {
      toast.error('Kampanya durdurulamadı');
    }
  };

  const handleDeleteCampaign = async () => {
    if (!window.confirm('Bu kampanyayı silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success('Kampanya silindi');
      navigate('/campaigns');
    } catch (error) {
      toast.error('Kampanya silinemedi');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Taslak', className: 'bg-zinc-100 text-zinc-800 border-zinc-200' },
      active: { label: 'Aktif', className: 'bg-emerald-100 text-emerald-800 border-emerald-200 status-pulse' },
      paused: { label: 'Duraklatıldı', className: 'bg-amber-100 text-amber-800 border-amber-200' },
      completed: { label: 'Tamamlandı', className: 'bg-blue-100 text-blue-800 border-blue-200' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getVoiceStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-emerald-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-rose-600" />;
      case 'calling':
        return <Phone className="w-4 h-4 text-blue-600 animate-pulse" />;
      default:
        return <Clock className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getFallbackIcon = (channel) => {
    switch (channel) {
      case 'sms':
        return <MessageSquare className="w-4 h-4" />;
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'whatsapp':
        return <MessageSquare className="w-4 h-4" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!campaign) return null;

  const stats = campaign.stats || {};
  const totalProcessed = (stats.voice_success || 0) + (stats.voice_failed || 0);
  const progressPercent = stats.total > 0 ? (totalProcessed / stats.total) * 100 : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaign-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/campaigns')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{campaign.name}</h1>
              {getStatusBadge(campaign.status)}
              {refreshing && <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />}
            </div>
            <p className="text-muted-foreground text-sm mt-1">{campaign.message}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(campaign.status === 'draft' || campaign.status === 'paused') && (
            <Button onClick={handleStartCampaign} data-testid="start-campaign-btn">
              <Play className="w-4 h-4 mr-2" />
              {campaign.status === 'paused' ? 'Devam Et' : 'Başlat'}
            </Button>
          )}
          {campaign.status === 'active' && (
            <Button variant="outline" onClick={handlePauseCampaign} data-testid="pause-campaign-btn">
              <Pause className="w-4 h-4 mr-2" />
              Duraklat
            </Button>
          )}
          <Button 
            variant="ghost" 
            onClick={handleDeleteCampaign}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            data-testid="delete-campaign-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Progress */}
      {campaign.status !== 'draft' && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">İlerleme</span>
              <span className="text-sm text-muted-foreground">
                {totalProcessed} / {stats.total || 0} kişi işlendi
              </span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card data-testid="stat-total">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total || 0}</p>
                <p className="text-xs text-muted-foreground">Toplam Kişi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-voice-success">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Phone className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-700">{stats.voice_success || 0}</p>
                <p className="text-xs text-muted-foreground">Başarılı Arama</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-sms">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0F172A]/10 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-[#0F172A]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.sms_sent || 0}</p>
                <p className="text-xs text-muted-foreground">SMS Gönderildi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-email">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-zinc-700" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.email_sent || 0}</p>
                <p className="text-xs text-muted-foreground">Email Gönderildi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-whatsapp">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#F97316]/10 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-[#F97316]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.whatsapp_sent || 0}</p>
                <p className="text-xs text-muted-foreground">WhatsApp Gönderildi</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Call Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Arama Kayıtları</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="p-8 text-center">
              <Phone className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-1">Henüz arama kaydı yok</h3>
              <p className="text-muted-foreground">
                Kampanyayı başlattığınızda arama kayıtları burada görünecek
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kişi</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>Sesli Arama</TableHead>
                  <TableHead>Fallback</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Tarih</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log, index) => (
                  <TableRow 
                    key={log.id}
                    className="stagger-item"
                    style={{ animationDelay: `${index * 0.03}s` }}
                    data-testid={`log-row-${log.id}`}
                  >
                    <TableCell className="font-medium">{log.contact_name}</TableCell>
                    <TableCell>{log.contact_phone}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getVoiceStatusIcon(log.voice_status)}
                        <span className="capitalize text-sm">
                          {log.voice_status === 'success' ? 'Başarılı' : 
                           log.voice_status === 'failed' ? 'Başarısız' :
                           log.voice_status === 'calling' ? 'Aranıyor...' : 'Bekliyor'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {log.fallback_channel ? (
                        <div className="flex items-center gap-2">
                          {getFallbackIcon(log.fallback_channel)}
                          <span className="uppercase text-sm">{log.fallback_channel}</span>
                          {log.fallback_status === 'sent' && (
                            <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs">
                              Gönderildi
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.fallback_provider ? (
                        <Badge variant="secondary" className="text-xs capitalize">
                          {log.fallback_provider}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(log.updated_at).toLocaleString('tr-TR')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
